import { useEffect, useState } from 'react'
import { useUpdateTenantSettings } from '@/features/company/queries'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import type { TenantSettingsPatch } from '@/features/company/types'

const HEX_RE = /^#[0-9a-fA-F]{6}$/

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined
    return body?.error || err.message || fallback
  }
  return err instanceof Error ? err.message : fallback
}

/**
 * Shared state machine for every "pick a colour and save it on the
 * tenant" field on the Branding page. Captures the saved-vs-draft-vs-
 * typed-input dance + the save/reset toasts so each picker section
 * doesn't have to re-implement it.
 */
export function useColorField<K extends keyof TenantSettingsPatch>(opts: {
  /** Column on `tenants` that this field writes to. */
  column: K
  /** The current saved value from the tenant settings query, or null. */
  saved: string | null
  /** The hex used when nothing is saved (i.e. the default in tokens.css). */
  defaultHex: string
  /** Toast title to show on a successful save. */
  savedTitle: string
  /** Toast title to show on a successful reset. */
  resetTitle: string
}) {
  const { column, saved, defaultHex, savedTitle, resetTitle } = opts
  const update = useUpdateTenantSettings()
  const { toast } = useToast()

  const [draft, setDraft] = useState<string>(saved || defaultHex)
  const [hexInput, setHexInput] = useState<string>(saved || defaultHex)

  // Re-sync when the server value changes (e.g. another tab saved).
  useEffect(() => {
    const next = saved || defaultHex
    setDraft(next)
    setHexInput(next)
  }, [saved, defaultHex])

  const onPick = (hex: string) => {
    setDraft(hex)
    setHexInput(hex)
  }

  const onHexChange = (next: string) => {
    setHexInput(next)
    if (HEX_RE.test(next.trim())) setDraft(next.trim().toLowerCase())
  }

  const onSave = async () => {
    if (!HEX_RE.test(hexInput.trim())) return
    try {
      await update.mutateAsync({ [column]: draft.toLowerCase() } as TenantSettingsPatch)
      toast({ title: savedTitle })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: `Failed to update ${column}`,
        description: messageFor(err, ''),
      })
    }
  }

  const onReset = async () => {
    try {
      await update.mutateAsync({ [column]: null } as TenantSettingsPatch)
      toast({ title: resetTitle })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: `Failed to reset ${column}`,
        description: messageFor(err, ''),
      })
    }
  }

  return {
    draft,
    hexInput,
    setDraft,
    setHexInput,
    onPick,
    onHexChange,
    onSave,
    onReset,
    busy: update.isPending,
    isValid: HEX_RE.test(hexInput.trim()),
  }
}
